import { useState } from 'react';
import { Accordion } from 'components/Accordion';
import { CallStatistic } from 'types/call.type';
import styles from './Statistic.module.scss';

export const Statistic = ({ callStats }: { callStats?: CallStatistic }) => {
    const [isAccordionOpen, setIsAccordionOpen] = useState(true);

    const {
        overDue,
        pending,
        ringing,
        inProgress,
        failed,
        busy,
        noAnswer,
        voicemail,
        voicemailSuccess,
        voicemailFailed,
        userAbandoned,
        agentAbandoned,
        transferCompleted,
        transferFailed,
        transferInProgress,
        transferUserAbandoned,
        noAgentsAvailable,
        agentRejected,
        humanOnCall,
        total,
        transfer,
        badNumber,
    } = callStats || {
        pending: 0,
        overDue: 0,
        ringing: 0,
        inProgress: 0,
        failed: 0,
        busy: 0,
        noAnswer: 0,
        voicemail: 0,
        voicemailSuccess: 0,
        voicemailFailed: 0,
        userAbandoned: 0,
        agentAbandoned: 0,
        transferCompleted: 0,
        transferFailed: 0,
        transferInProgress: 0,
        transferUserAbandoned: 0,
        noAgentsAvailable: 0,
        agentRejected: 0,
        humanOnCall: 0,
        total: 0,
        transfer: 0,
        badNumber: 0,
    };

    return (
        <div className={styles.callStatistics}>
            <Accordion
                isOpen={isAccordionOpen}
                onToggle={() => setIsAccordionOpen(!isAccordionOpen)}
                header={
                    <div className={styles.totalCallsLabel}>
                        <span>Call status overview:</span>
                        <span> {total}</span>
                    </div>
                }
                className={styles.accordion}
            >
                <div className={styles.statsCardsGrid}>
                    <div className={`${styles.statCard} ${styles.pendingCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>Queued/Overdue</div>
                            <div className={styles.statValue}>{overDue + pending}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Queued:</span>
                                <span className={styles.noteValue}>{pending}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Overdue:</span>
                                <span className={styles.noteValue}>{overDue}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.inProgressCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>In Progress</div>
                            <div className={styles.statValue}>{ringing + inProgress}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Ringing:</span>
                                <span className={styles.noteValue}>{ringing}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Active:</span>
                                <span className={styles.noteValue}>{inProgress}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.unableCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>Unable to contact</div>
                            <div className={styles.statValue}>{failed + busy + noAnswer + badNumber}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Failed:</span>
                                <span className={styles.noteValue}>{failed}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Busy:</span>
                                <span className={styles.noteValue}>{busy}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>No Answer:</span>
                                <span className={styles.noteValue}>{noAnswer}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Bad Number:</span>
                                <span className={styles.noteValue}>{badNumber}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.voicemailCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>Voicemail</div>
                            <div className={styles.statValue}>{voicemail}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Success:</span>
                                <span className={styles.noteValue}>{voicemailSuccess}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Failed:</span>
                                <span className={styles.noteValue}>{voicemailFailed}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.contactedCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>Contacted</div>
                            <div className={styles.statValue}>{userAbandoned + agentAbandoned}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>User Abandoned:</span>
                                <span className={styles.noteValue}>{userAbandoned}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Agent Abandoned:</span>
                                <span className={styles.noteValue}>{agentAbandoned}</span>
                            </div>
                        </div>
                    </div>

                    <div className={`${styles.statCard} ${styles.transferCard}`}>
                        <div className={styles.statCardHeader}>
                            <div className={styles.statLabel}>Transfer</div>
                            <div className={styles.statValue}>{transfer}</div>
                        </div>
                        <div className={styles.statNotes}>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Completed:</span>
                                <span className={styles.noteValue}>{transferCompleted}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Failed:</span>
                                <span className={styles.noteValue}>{transferFailed}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>In Progress:</span>
                                <span className={styles.noteValue}>{transferInProgress}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>No Agents:</span>
                                <span className={styles.noteValue}>{noAgentsAvailable}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Agent Rejected:</span>
                                <span className={styles.noteValue}>{agentRejected}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>User Abandoned:</span>
                                <span className={styles.noteValue}>{transferUserAbandoned}</span>
                            </div>
                            <div className={styles.statNote}>
                                <span className={styles.noteLabel}>Human On Call:</span>
                                <span className={styles.noteValue}>{humanOnCall}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Accordion>
        </div>
    );
};
